import './OfferCell.scss';
import React, { useRef } from 'react';
import clsx from 'clsx';
import Tooltip from '@srm/ui/tooltip';
import { MdStars } from 'react-icons/md';
import { IOffer, ITableDemandItem, ITableDemandSettings } from '@models/winnerSelection/ITableDemand';
import OfferCritDetails, { IOfferCritDetailsHandle } from '../OfferCritDetails';
import { getEffectiveCritType } from '../utils/ratingScales';
import { checkIsRatingWinner } from '../utils/winnerLogic';
import { getOfferAnswerControlType } from '../utils/answerTypes';
import { formatOfferAnswerValue } from '../utils/formatters';

interface IProps {
    crit: ITableDemandItem;
    offer: IOffer;
    settings: ITableDemandSettings;
    /** ID родительского документа (позиция) — для модалки OfferCritDetails. */
    demandId: string;
    /** ID таблицы из настроек — для модалки OfferCritDetails. */
    tableKey: string;
    /** Колбэк reload после сохранения изменений в OfferCritDetails. */
    onReload: () => void;
}

/**
 * Ячейка оценки одного предложения по одному критерию.
 *
 * 3 режима:
 * - isInfo: full-width комментарий, без блока балла, без winner-подсветки.
 * - isUnrated: штриховой фон, «—» вместо рейтинга, всегда видна glass-pill «Подробнее → Оценить».
 * - standard: рейтинг с balls/5 + обоснование с clamp.
 *
 * Лидер по критерию (через checkIsRatingWinner) получает aurora-tint и крон-иконку.
 *
 * Memo: между reload'ами все пропсы ссылочно стабильны (crit/offer/settings — из
 * tableData; onReload — useCallback в useTableDemandData). Лишний ре-рендер
 * матрицы 50×30 = 1500 ячеек при ре-рендере parent'а (открытие/закрытие модалок,
 * скролл) отсекается.
 */
const OfferCellInner: React.FC<IProps> = ({ crit, offer, settings, demandId, tableKey, onReload }) => {
    const offerCrit = crit.offers[offer.key];
    const effType = getEffectiveCritType(crit);
    const isInfo = effType === 'info';
    const isAnswerOnly = effType === 'answer';
    const showScoreBlock = effType === 'score';
    const showAnswer = showScoreBlock && settings.showAnswerInScore;
    const isCustomScale = settings.customScaleCrits?.includes(crit.critName) ?? false;
    const answerControlType = getOfferAnswerControlType(crit, settings.offerAnswerSettings);

    const isUnrated =
        showScoreBlock && offerCrit?.rating == null && offerCrit?.score == null && (offerCrit?.allowEdit ?? false);

    const isCellWinner = showScoreBlock && !isUnrated && checkIsRatingWinner(crit, offer.key);

    const descriptionHeader = isInfo ? 'Комментарий' : isAnswerOnly ? 'Ответ' : 'Обоснование';
    const description = isAnswerOnly
        ? formatOfferAnswerValue(offerCrit?.answer, answerControlType)
        : offerCrit?.description;

    const cellRef = useRef<HTMLDivElement>(null);
    const detailsRef = useRef<IOfferCritDetailsHandle>(null);

    /**
     * Клик по всей ячейке открывает детали критерия (раньше кликабелен был лишь
     * маленький pill «Подробнее»). Модалка деталей портируется Mantine в body, но
     * её синтетические клики всплывают по React-дереву обратно сюда — поэтому
     * открываем только если клик реально пришёлся на DOM-узел ячейки.
     */
    const handleCellClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (cellRef.current?.contains(e.target as Node)) {
            detailsRef.current?.open();
        }
    };

    const handleCellKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        // Тот же guard, что и в handleCellClick: клавиши из портированной в body
        // модалки всплывают по React-дереву обратно сюда. Без проверки target
        // пробел в textarea «Обоснование/Комментарий» отменяется preventDefault'ом.
        if (!cellRef.current?.contains(e.target as Node)) return;
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            detailsRef.current?.open();
        }
    };

    return (
        <div
            ref={cellRef}
            className={clsx('ws-cell', {
                'ws-cell--winner': isCellWinner,
                'ws-cell--info': isInfo,
                'ws-cell--answer': isAnswerOnly,
                'ws-cell--unrated': isUnrated,
            })}
            role="button"
            tabIndex={0}
            aria-label={`Подробнее: критерий «${crit.critName}»`}
            onClick={handleCellClick}
            onKeyDown={handleCellKeyDown}
        >
            {showScoreBlock && (
                <div className="ws-cell__score">
                    <div className="ws-cell__score-label">{isCellWinner ? 'Лидер' : 'Рейтинг'}</div>
                    <div className="ws-cell__score-rating">
                        {isUnrated
                            ? '—'
                            : offerCrit?.rating != null
                              ? offerCrit.rating.toLocaleString('ru-RU', { maximumFractionDigits: 2 })
                              : '—'}
                        {isCellWinner && (
                            <span className="ws-cell__score-crown" aria-label="Лидер">
                                <MdStars size="13px" />
                            </span>
                        )}
                    </div>
                    {!isUnrated && offerCrit?.score != null && (
                        <>
                            <div className="ws-cell__score-divider" aria-hidden />
                            <div className="ws-cell__score-sublabel">Балл</div>
                            <Tooltip
                                openDelay={100}
                                background="black"
                                position="bottom"
                                label="Балл (фактическая оценка)"
                            >
                                <div className="ws-cell__score-line">
                                    <b>{offerCrit.score.toLocaleString('ru-RU', { maximumFractionDigits: 4 })}</b>
                                    {!isCustomScale && <span> / 5</span>}
                                </div>
                            </Tooltip>
                        </>
                    )}
                    {showAnswer && offerCrit?.answer && (
                        <div className="ws-cell__score-line ws-cell__score-line--answer">
                            ответ: <b>{offerCrit.answer}</b>
                        </div>
                    )}
                </div>
            )}

            <div className="ws-cell__body">
                <div className="ws-cell__body-label">{descriptionHeader}</div>
                <div className="ws-cell__rationale">{description || (isUnrated ? 'не оценено' : '—')}</div>
            </div>

            <OfferCritDetails
                ref={detailsRef}
                tableKey={tableKey}
                isEdit={offerCrit?.allowEdit ?? false}
                disabledHint={settings.offerCritDisabledHint}
                ratingScaleHints={settings.ratingScaleHints}
                customScaleCrits={settings.customScaleCrits}
                editButtonName={settings.editOfferCritButtonName ?? 'Подробнее'}
                offerAnswerSettings={settings.offerAnswerSettings}
                showAnswerInScore={settings.showAnswerInScore}
                allowGroupActions={offerCrit?.allowGroupEdit ?? false}
                offerId={offer.key}
                demandId={demandId}
                value={offerCrit}
                rowData={crit}
                onSubmit={onReload}
            />
        </div>
    );
};

export const OfferCell = React.memo(OfferCellInner);
